<template>
  <el-dialog
    v-model="visible"
    :show-close="false"
    width="640px"
    class="global-search-dialog !rounded-2xl !p-0 overflow-hidden"
    :append-to-body="true"
    @opened="onOpened"
  >
    <div class="search-modal-container bg-[var(--surface)] text-[var(--text-main)] rounded-2xl border border-[var(--glass-border)] shadow-2xl overflow-hidden">
      <!-- Search Bar Input Header -->
      <div class="p-4 border-b border-[var(--glass-border)] flex items-center gap-3 bg-white/[0.02]">
        <el-icon class="text-xl text-profissa-accent"><Search /></el-icon>
        <input
          ref="inputRef"
          v-model="query"
          type="text"
          class="flex-1 bg-transparent border-none outline-none text-base sm:text-lg text-[var(--text-main)] placeholder-[var(--text-dim)] font-medium"
          placeholder="Rechercher une page, un artisan, un client, un code mission..."
          @keydown.down.prevent="navigateResults(1)"
          @keydown.up.prevent="navigateResults(-1)"
          @keydown.enter.prevent="selectCurrent"
          @keydown.esc="close"
        />
        <el-icon v-if="isLoading" class="is-loading text-lg text-profissa-accent"><Loading /></el-icon>
        <button
          v-else-if="query"
          class="text-xs text-[var(--text-dim)] hover:text-[var(--text-main)] transition-colors p-1"
          @click="query = ''"
        >
          <el-icon class="text-sm"><Close /></el-icon>
        </button>
        <kbd class="hidden sm:inline-flex items-center px-2 py-0.5 text-[0.7rem] font-mono text-[var(--text-dim)] bg-white/5 border border-[var(--glass-border)] rounded">ESC</kbd>
      </div>

      <!-- Results Body -->
      <div class="max-h-[380px] overflow-y-auto p-2 scrollbar-thin">
        <!-- Empty query initial recommendations: Top pages -->
        <div v-if="!query.trim()" class="p-3">
          <div class="text-[0.7rem] font-bold uppercase tracking-wider text-[var(--text-dim)] px-2 mb-2">
            Navigation Rapide
          </div>
          <div class="flex flex-col gap-1">
            <div
              v-for="(item, idx) in accessiblePages.slice(0, 8)"
              :key="item.id"
              class="flex items-center justify-between px-3 py-2.5 rounded-xl cursor-pointer transition-colors"
              :class="selectedIndex === idx ? 'bg-profissa-accent/10 text-profissa-accent border border-profissa-accent/30' : 'hover:bg-white/5 text-[var(--text-main)]'"
              @mouseenter="selectedIndex = idx"
              @click="goToPage(item)"
            >
              <div class="flex items-center gap-3">
                <el-icon class="text-lg"><component :is="item.icon" /></el-icon>
                <span class="font-semibold text-sm">{{ item.label }}</span>
              </div>
              <span class="text-xs text-[var(--text-dim)] bg-white/5 px-2 py-0.5 rounded">{{ item.group }}</span>
            </div>
          </div>
        </div>

        <!-- No results found -->
        <div v-else-if="allResults.length === 0 && !isLoading" class="py-10 text-center text-[var(--text-dim)]">
          <el-icon class="text-3xl mb-2"><Search /></el-icon>
          <div class="text-sm">Aucun résultat trouvé pour « <span class="text-[var(--text-main)] font-semibold">{{ query }}</span> »</div>
          <div class="text-xs mt-1">Essayez un nom, un numéro burkinabè ou un code mission</div>
        </div>

        <!-- Grouped Results -->
        <div v-else class="flex flex-col gap-3 p-1">
          <!-- 1. Pages / Navigation -->
          <div v-if="filteredPages.length">
            <div class="text-[0.7rem] font-bold uppercase tracking-wider text-[var(--text-dim)] px-3 mb-1.5">
              Pages & Menus ({{ filteredPages.length }})
            </div>
            <div
              v-for="item in filteredPages"
              :key="'page-' + item.id"
              class="flex items-center justify-between px-3 py-2 rounded-xl cursor-pointer transition-colors"
              :class="selectedIndex === getAbsoluteIndex(item) ? 'bg-profissa-accent/15 text-profissa-accent border border-profissa-accent/30' : 'hover:bg-white/5 text-[var(--text-main)]'"
              @mouseenter="selectedIndex = getAbsoluteIndex(item)"
              @click="executeItem(item)"
            >
              <div class="flex items-center gap-3">
                <el-icon class="text-lg"><component :is="item.icon" /></el-icon>
                <span class="font-semibold text-sm">{{ item.label }}</span>
              </div>
              <span class="text-xs text-[var(--text-dim)] bg-white/5 px-2 py-0.5 rounded">{{ item.group }}</span>
            </div>
          </div>

          <!-- 2. Artisans -->
          <div v-if="artisanResults.length">
            <div class="text-[0.7rem] font-bold uppercase tracking-wider text-[var(--text-dim)] px-3 mb-1.5">
              Artisans ({{ artisanResults.length }})
            </div>
            <div
              v-for="art in artisanResults"
              :key="'art-' + art.id"
              class="flex items-center justify-between px-3 py-2 rounded-xl cursor-pointer transition-colors"
              :class="selectedIndex === getAbsoluteIndex(art) ? 'bg-profissa-accent/15 text-profissa-accent border border-profissa-accent/30' : 'hover:bg-white/5 text-[var(--text-main)]'"
              @mouseenter="selectedIndex = getAbsoluteIndex(art)"
              @click="executeItem(art)"
            >
              <div class="flex items-center gap-3">
                <div class="w-8 h-8 rounded-lg bg-profissa-accent/10 border border-profissa-accent/20 flex items-center justify-center font-bold text-profissa-accent text-xs">
                  {{ (art.full_name || art.phone || 'A')[0].toUpperCase() }}
                </div>
                <div>
                  <div class="font-bold text-sm leading-tight">{{ art.full_name || 'Sans nom' }}</div>
                  <div class="text-xs text-[var(--text-dim)] font-mono">{{ formatPhone(art.phone) }}</div>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <span v-if="art.category_name" class="text-xs bg-white/5 px-2 py-0.5 rounded text-[var(--text-muted)]">{{ art.category_name }}</span>
                <el-tag size="small" :type="art.approval_status === 'approved' ? 'success' : (art.approval_status === 'rejected' ? 'danger' : 'warning')">
                  {{ art.approval_status === 'approved' ? 'Validé' : (art.approval_status === 'rejected' ? 'Rejeté' : 'En attente') }}
                </el-tag>
              </div>
            </div>
          </div>

          <!-- 3. Clients -->
          <div v-if="clientResults.length">
            <div class="text-[0.7rem] font-bold uppercase tracking-wider text-[var(--text-dim)] px-3 mb-1.5">
              Clients ({{ clientResults.length }})
            </div>
            <div
              v-for="cli in clientResults"
              :key="'cli-' + cli.id"
              class="flex items-center justify-between px-3 py-2 rounded-xl cursor-pointer transition-colors"
              :class="selectedIndex === getAbsoluteIndex(cli) ? 'bg-profissa-accent/15 text-profissa-accent border border-profissa-accent/30' : 'hover:bg-white/5 text-[var(--text-main)]'"
              @mouseenter="selectedIndex = getAbsoluteIndex(cli)"
              @click="executeItem(cli)"
            >
              <div class="flex items-center gap-3">
                <div class="w-8 h-8 rounded-lg bg-white/5 border border-[var(--glass-border)] flex items-center justify-center font-bold text-xs text-[var(--text-main)]">
                  {{ (cli.full_name || cli.phone || 'C')[0].toUpperCase() }}
                </div>
                <div>
                  <div class="font-bold text-sm leading-tight">{{ cli.full_name || 'Client' }}</div>
                  <div class="text-xs text-[var(--text-dim)] font-mono">{{ formatPhone(cli.phone) }}</div>
                </div>
              </div>
              <el-tag size="small" :type="cli.is_active ? 'success' : 'danger'">
                {{ cli.is_active ? 'Actif' : 'Suspendu' }}
              </el-tag>
            </div>
          </div>

          <!-- 4. Missions -->
          <div v-if="missionResults.length">
            <div class="text-[0.7rem] font-bold uppercase tracking-wider text-[var(--text-dim)] px-3 mb-1.5">
              Missions & Urgences ({{ missionResults.length }})
            </div>
            <div
              v-for="mis in missionResults"
              :key="'mis-' + mis.id"
              class="flex items-center justify-between px-3 py-2 rounded-xl cursor-pointer transition-colors"
              :class="selectedIndex === getAbsoluteIndex(mis) ? 'bg-profissa-accent/15 text-profissa-accent border border-profissa-accent/30' : 'hover:bg-white/5 text-[var(--text-main)]'"
              @mouseenter="selectedIndex = getAbsoluteIndex(mis)"
              @click="executeItem(mis)"
            >
              <div class="flex items-center gap-3">
                <el-icon class="text-profissa-accent"><List /></el-icon>
                <div>
                  <div class="font-bold text-sm leading-tight font-mono">#{{ mis.id?.slice(0, 8) }}…</div>
                  <div class="text-xs text-[var(--text-dim)] flex items-center gap-1.5 flex-wrap mt-0.5">
                    <span>{{ mis.category_name || 'Intervention' }}</span>
                    <span v-if="mis.client_name">· Clt: {{ mis.client_name }}</span>
                    <span v-if="mis.artisan_name">· Art: {{ mis.artisan_name }}</span>
                  </div>
                </div>
              </div>
              <el-tag size="small" :type="missionStatusBadge(mis.status).type">
                {{ missionStatusBadge(mis.status).label }}
              </el-tag>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer Guide -->
      <div class="px-4 py-2.5 bg-black/20 border-t border-[var(--glass-border)] flex items-center justify-between text-xs text-[var(--text-dim)]">
        <div class="flex items-center gap-3">
          <span class="flex items-center gap-1"><kbd class="px-1.5 py-0.5 bg-white/5 border border-[var(--glass-border)] rounded text-[0.65rem] font-mono">↑</kbd><kbd class="px-1.5 py-0.5 bg-white/5 border border-[var(--glass-border)] rounded text-[0.65rem] font-mono">↓</kbd> Naviguer</span>
          <span class="flex items-center gap-1"><kbd class="px-1.5 py-0.5 bg-white/5 border border-[var(--glass-border)] rounded text-[0.65rem] font-mono">↵</kbd> Ouvrir</span>
        </div>
        <div>
          <span class="text-profissa-accent font-semibold">Profissa Spotlight</span>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount, defineProps, defineEmits } from 'vue'
import { useRouter } from 'vue-router'
import { Search, Loading, Close, List } from '@element-plus/icons-vue'
import { navItemsForUser } from '../../navigation/adminNavigation'
import { searchArtisans, listClients, listInterventions } from '../../services/adminApi'
import { formatPhone, matchBurkinaPhone } from '../../utils/format'
import { missionStatusBadge } from '../../utils/labels'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  currentUser: { type: Object, default: () => null },
})
const emit = defineEmits(['update:modelValue', 'navigate'])

const router = useRouter()
const visible = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
})

const query = ref('')
const inputRef = ref(null)
const isLoading = ref(false)
const selectedIndex = ref(0)

const artisanResults = ref([])
const clientResults = ref([])
const missionResults = ref([])

// Accessible navigation pages
const accessiblePages = computed(() => {
  return navItemsForUser(props.currentUser)
})

// Filtered pages based on search text
const filteredPages = computed(() => {
  const q = query.value.trim().toLowerCase()
  if (!q) return []
  return accessiblePages.value.filter((p) => {
    return p.label.toLowerCase().includes(q) ||
           p.group.toLowerCase().includes(q) ||
           p.id.toLowerCase().includes(q)
  })
})

// All combined flat results for keyboard arrow indexing
const allResults = computed(() => {
  if (!query.value.trim()) {
    return accessiblePages.value.slice(0, 8).map(p => ({ ...p, _type: 'page' }))
  }
  const res = []
  filteredPages.value.forEach(p => res.push({ ...p, _type: 'page' }))
  artisanResults.value.forEach(a => res.push({ ...a, _type: 'artisan' }))
  clientResults.value.forEach(c => res.push({ ...c, _type: 'client' }))
  missionResults.value.forEach(m => res.push({ ...m, _type: 'mission' }))
  return res
})

function getAbsoluteIndex(item) {
  return allResults.value.findIndex(r => r === item || (r.id === item.id && r._type === item._type))
}

let searchTimer = null
let currentSearchSeq = 0

watch(query, (newQ) => {
  selectedIndex.value = 0
  clearTimeout(searchTimer)
  const q = newQ.trim()
  if (q.length < 2) {
    artisanResults.value = []
    clientResults.value = []
    missionResults.value = []
    isLoading.value = false
    return
  }

  isLoading.value = true
  const seq = ++currentSearchSeq

  searchTimer = setTimeout(async () => {
    try {
      const perms = props.currentUser?.permissions || []
      const promises = []
      const qLower = q.toLowerCase()
      const hasDigits = /\d/.test(q)
      const isPureNumber = /^\+?\d+$/.test(q.replace(/\s+/g, ''))

      // 1. Search Artisans
      if (perms.includes('artisans.read.any')) {
        promises.push(
          searchArtisans(q).then(res => {
            if (seq !== currentSearchSeq) return
            const filtered = (res || []).filter(a => {
              const nameMatch = (a.full_name || '').toLowerCase().includes(qLower)
              const catMatch = (a.category_name || '').toLowerCase().includes(qLower)
              const phoneMatch = hasDigits ? (matchBurkinaPhone(a.phone, q) || matchBurkinaPhone(a.whatsapp, q)) : false
              if (isPureNumber) {
                return phoneMatch
              }
              return nameMatch || catMatch || phoneMatch
            })
            artisanResults.value = filtered.slice(0, 5).map(a => ({ ...a, _type: 'artisan' }))
          }).catch(() => { if (seq === currentSearchSeq) artisanResults.value = [] })
        )
      }

      // 2. Search Clients
      if (perms.includes('clients.read.any')) {
        promises.push(
          listClients(q).then(res => {
            if (seq !== currentSearchSeq) return
            const filtered = (res || []).filter(c => {
              const nameMatch = (c.full_name || '').toLowerCase().includes(qLower)
              const phoneMatch = hasDigits ? matchBurkinaPhone(c.phone, q) : false
              if (isPureNumber) {
                return phoneMatch
              }
              return nameMatch || phoneMatch
            })
            clientResults.value = filtered.slice(0, 5).map(c => ({ ...c, _type: 'client' }))
          }).catch(() => { if (seq === currentSearchSeq) clientResults.value = [] })
        )
      }

      // 3. Search Missions
      if (perms.includes('missions.read.any')) {
        promises.push(
          listInterventions(q).then(res => {
            if (seq !== currentSearchSeq) return
            const filtered = (res || []).filter(m => {
              const idMatch = (m.id || '').toLowerCase().includes(qLower)
              const catMatch = (m.category_name || '').toLowerCase().includes(qLower)
              const clientNameMatch = (m.client_name || '').toLowerCase().includes(qLower)
              const artisanNameMatch = (m.artisan_name || '').toLowerCase().includes(qLower)
              const clientPhoneMatch = hasDigits ? matchBurkinaPhone(m.client_phone, q) : false
              const artisanPhoneMatch = hasDigits ? matchBurkinaPhone(m.artisan_phone, q) : false
              if (isPureNumber) {
                return idMatch || clientPhoneMatch || artisanPhoneMatch
              }
              return idMatch || catMatch || clientNameMatch || artisanNameMatch || clientPhoneMatch || artisanPhoneMatch
            })
            missionResults.value = filtered.slice(0, 5).map(m => ({ ...m, _type: 'mission' }))
          }).catch(() => { if (seq === currentSearchSeq) missionResults.value = [] })
        )
      }

      await Promise.allSettled(promises)
    } finally {
      if (seq === currentSearchSeq) {
        isLoading.value = false
      }
    }
  }, 300)
})

function onOpened() {
  selectedIndex.value = 0
  nextTick(() => {
    if (inputRef.value) inputRef.value.focus()
  })
}

function close() {
  visible.value = false
}

function navigateResults(direction) {
  if (!allResults.value.length) return
  selectedIndex.value = (selectedIndex.value + direction + allResults.value.length) % allResults.value.length
}

function selectCurrent() {
  if (allResults.value.length > 0 && selectedIndex.value >= 0 && selectedIndex.value < allResults.value.length) {
    executeItem(allResults.value[selectedIndex.value])
  }
}

function goToPage(page) {
  close()
  emit('navigate', page.path)
  router.push(page.path)
}

function executeItem(item) {
  close()
  if (item._type === 'page' || (!item._type && item.path)) {
    emit('navigate', item.path)
    router.push(item.path)
  } else if (item._type === 'artisan') {
    emit('navigate', '/artisans')
    router.push({ path: '/artisans', query: { q: item.phone || item.full_name } })
  } else if (item._type === 'client') {
    emit('navigate', '/clients')
    router.push({ path: '/clients', query: { q: item.phone || item.full_name } })
  } else if (item._type === 'mission') {
    emit('navigate', '/missions')
    router.push({ path: '/missions', query: { q: item.id } })
  }
}

// Global hotkey Ctrl+K / Cmd+K
function onGlobalKeyDown(e) {
  if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
    e.preventDefault()
    visible.value = !visible.value
  }
}

onMounted(() => {
  window.addEventListener('keydown', onGlobalKeyDown)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onGlobalKeyDown)
})
</script>

<style>
.global-search-dialog .el-dialog__header,
.global-search-dialog .el-dialog__body {
  padding: 0 !important;
}
.global-search-dialog {
  background: transparent !important;
  box-shadow: none !important;
}
</style>
